/**
 * Card hand UI (HANDOFF §3.6 / §6 Agent E / §8).
 *
 * Builds two DOM rows — the offense (clubs) row and the defense row — from game
 * state and returns an `update(state)`. Tapping an AFFORDABLE card dispatches the
 * matching action:
 *   - offense card → `PlayOffense{cardIdx}` (enters the aim phase; §3.4)
 *   - defense card → `PlayDefense{cardIdx}` (drops a hazard / rough / wind; §3.7)
 *
 * Cards the side cannot currently afford (cost > floor(energy), §3.6) render
 * disabled and ignore taps. Rarity is shown as a coloured dot per §8. This module
 * reads state for display and EMITS actions only — it never mutates the sim.
 */
import type { Club, DefenseCard, GameState, Side } from '../sim/types.ts';
import { DEFENSE_CHARGE } from '../sim/constants.ts';
import { fillCardFace } from './cardFace.ts';

/** What the hand needs from the host: a way to emit actions + read state. */
export interface HandDeps {
  dispatch(action: { type: 'PlayOffense' | 'PlayDefense'; cardIdx: number }): void;
  getState?(): GameState;
}

export interface CardHand {
  /** Reflect energy/affordability + (rarely) hand contents into the DOM. */
  update(state: GameState): void;
  /** The root element the hand was mounted into. */
  readonly el: HTMLElement;
}

/** A single rendered card's live element references, so update() is allocation-free. */
interface CardCell {
  readonly root: HTMLButtonElement;
  cost: number;
  id: string;
}

function affordable(cost: number, energy: number): boolean {
  return cost <= Math.floor(energy);
}

/**
 * Build one hand row into `row` from `cards`, wiring each card's tap to dispatch
 * `Play${kind}{idx}`. Returns the per-card cells for cheap updates.
 */
function buildRow(
  row: HTMLElement,
  cards: readonly (Club | DefenseCard)[],
  kind: 'PlayOffense' | 'PlayDefense',
  rowClass: 'off' | 'def',
  dispatch: HandDeps['dispatch'],
): CardCell[] {
  row.replaceChildren();
  const cells: CardCell[] = [];
  cards.forEach((card, idx) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `fc-card fc-card--${rowClass}`;
    btn.dataset.idx = String(idx);

    fillCardFace(btn, card);
    btn.addEventListener('click', () => {
      if (btn.disabled) return;
      dispatch({ type: kind, cardIdx: idx });
    });

    row.appendChild(btn);
    cells.push({ root: btn, cost: card.cost, id: card.id });
  });
  return cells;
}

/** True if the hand's card identities changed (a card was cycled). */
function handChanged(cells: readonly CardCell[], cards: readonly (Club | DefenseCard)[]): boolean {
  if (cells.length !== cards.length) return true;
  for (let i = 0; i < cells.length; i++) {
    if (cells[i]?.id !== cards[i]?.id) return true;
  }
  return false;
}

/** Apply affordable/disabled + selected styling without rebuilding the DOM. `enabled` is an extra
 *  gate (the defense row also needs a CHARGE) — when false every card in the row locks. */
function refreshRow(
  cells: readonly CardCell[],
  energy: number,
  selectedIdx: number | null,
  enabled = true,
): void {
  cells.forEach((cell, idx) => {
    const ok = enabled && affordable(cell.cost, energy);
    cell.root.disabled = !ok;
    cell.root.classList.toggle('is-locked', !ok);
    cell.root.classList.toggle('is-selected', selectedIdx === idx);
  });
}

/**
 * Create the card hand for a side (default `you`) mounted under `root`.
 *
 * The returned `update(state)` rebuilds a row only when its card identities
 * change (a card was played + cycled); otherwise it just refreshes affordability
 * and the selected-club highlight — cheap enough for every frame.
 */
export function createCardHand(root: HTMLElement, deps: HandDeps, side: Side = 'you'): CardHand {
  const el = document.createElement('div');
  el.className = 'fc-hand';

  // Offense in the LEFT column, defense in the RIGHT column (each a vertical stack).
  const offCol = document.createElement('div');
  offCol.className = 'fc-hand__col fc-hand__col--off';
  const offLabel = document.createElement('div');
  offLabel.className = 'fc-hand__label';
  offLabel.textContent = 'Attack';
  const offRow = document.createElement('div');
  offRow.className = 'fc-hand__row';
  offCol.append(offLabel, offRow);

  const defCol = document.createElement('div');
  defCol.className = 'fc-hand__col fc-hand__col--def';
  const defLabel = document.createElement('div');
  defLabel.className = 'fc-hand__label';
  defLabel.textContent = 'Defend';
  // Defense CHARGE pips (anti-turtle): filled = banked charges, earned by attacking. When dry,
  // the row locks. Shown right of the label so the player reads "can I defend?" at a glance.
  const defCharge = document.createElement('span');
  defCharge.className = 'fc-hand__charge';
  const pips: HTMLElement[] = [];
  for (let i = 0; i < DEFENSE_CHARGE.max; i++) {
    const pip = document.createElement('i');
    pip.className = 'fc-hand__pip';
    defCharge.appendChild(pip);
    pips.push(pip);
  }
  defLabel.appendChild(defCharge);
  const defRow = document.createElement('div');
  defRow.className = 'fc-hand__row';
  defCol.append(defLabel, defRow);

  el.append(offCol, defCol);
  root.appendChild(el);

  let offCells: CardCell[] = [];
  let defCells: CardCell[] = [];

  function update(state: GameState): void {
    const s = side === 'you' ? state.you : state.rival;
    const { offenseHand, defenseHand, energy } = s;

    if (handChanged(offCells, offenseHand)) {
      offCells = buildRow(offRow, offenseHand, 'PlayOffense', 'off', deps.dispatch);
    }
    if (handChanged(defCells, defenseHand)) {
      defCells = buildRow(defRow, defenseHand, 'PlayDefense', 'def', deps.dispatch);
    }

    // The selected club is whichever index is mid-swing for this side (§3.4).
    const selected =
      state.swing.side === side && state.swing.phase !== 'idle' ? state.swing.clubIdx : null;

    // Defense charges (anti-turtle): light up the pips + lock the row when dry.
    const charges = s.defenseCharges;
    pips.forEach((pip, i) => pip.classList.toggle('is-on', i < charges));
    defCharge.classList.toggle('is-dry', charges < 1);

    refreshRow(offCells, energy, selected);
    refreshRow(defCells, energy, null, charges >= 1);
  }

  return { update, el };
}
